import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import axios from 'axios';
import { ArrowLeft, Search, BookOpen, ExternalLink, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TemasPage = () => {
  const [temas, setTemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('Todos');
  const [error, setError] = useState('');

  const { isAuthenticated, userType, loading: authLoading } = useAuth();
  const { isDarkMode: darkMode } = useDarkMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get return URL from query params (where user came from)
  const returnUrl = searchParams.get('from') || '/';

  // Redirect if not authenticated (but wait for auth loading to complete)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch temas on component mount
  useEffect(() => {
    const fetchTemas = async () => {
      try {
        setLoading(true);
        
        // Get token from localStorage (more reliable than axios defaults)
        const token = localStorage.getItem('authToken');
        
        const response = await axios.get(`${API}/temas`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setTemas(response.data.temas);
      } catch (err) {
        console.error('Error fetching temas:', err);
        setError('Error cargando temas');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && !authLoading) {
      fetchTemas();
    }
  }, [isAuthenticated, authLoading]);

  // Group temas by first letter
  const groupedTemas = temas.reduce((acc, tema) => {
    const firstLetter = tema.title.charAt(0).toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(tema);
    return acc;
  }, {});

  // Filter temas based on search term and selected letter
  const filteredTemas = temas.filter(tema => {
    const matchesSearch = tema.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLetter = selectedLetter === 'Todos' || 
                         tema.title.charAt(0).toUpperCase() === selectedLetter;
    return matchesSearch && matchesLetter;
  });

  // Get available letters for filter
  const availableLetters = [...new Set(temas.map(tema => tema.title.charAt(0).toUpperCase()))].sort();

  const handleTemaClick = (temaId) => {
    navigate(`/tema/${temaId}`);
  };

  if (!authLoading && !isAuthenticated) {
    return null;
  }

  if (loading || authLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <Loader className="animate-spin h-8 w-8 text-purple-600 mx-auto mb-4" />
          <p className={darkMode ? 'text-gray-300' : 'text-gray-500'}>
            Cargando temas...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      {/* Header */}
      <div className={`shadow-sm border-b transition-colors duration-300 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-6xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate(returnUrl)}
            className={`flex items-center mb-4 transition-colors ${
              darkMode 
                ? 'text-gray-300 hover:text-white' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <ArrowLeft size={20} className="mr-2" />
            {returnUrl.includes('/volume/') ? 'Volver al Volumen' : 'Volver al inicio'}
          </button>
          
          <div className="flex items-center mb-6">
            <BookOpen size={32} className="text-purple-600 mr-3" />
            <h1 className={`text-3xl font-bold ${
              darkMode ? 'text-white' : 'text-gray-800'
            }`}>
              Temas del Libro de Cielo
            </h1>
          </div>

          {/* Search Bar */}
          <div className="relative mb-6">
            <Search size={20} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
              darkMode ? 'text-gray-400' : 'text-gray-400'
            }`} />
            <input
              type="text"
              placeholder="Buscar temas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-800 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Letter Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedLetter('Todos')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedLetter === 'Todos'
                  ? 'bg-purple-600 text-white'
                  : darkMode
                    ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Todos
            </button>
            {availableLetters.map(letter => (
              <button
                key={letter}
                onClick={() => setSelectedLetter(letter)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedLetter === letter
                    ? 'bg-purple-600 text-white'
                    : darkMode
                      ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className={`border px-4 py-3 rounded mb-6 transition-colors duration-300 ${
            darkMode 
              ? 'bg-red-900/20 border-red-400 text-red-300' 
              : 'bg-red-100 border-red-400 text-red-700'
          }`}>
            {error}
          </div>
        )}

        {filteredTemas.length === 0 && !loading ? (
          <div className="text-center py-12">
            <BookOpen size={48} className={`mx-auto mb-4 ${
              darkMode ? 'text-gray-600' : 'text-gray-300'
            }`} />
            <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
              {searchTerm || selectedLetter !== 'Todos' 
                ? 'No se encontraron temas con los filtros aplicados'
                : 'No hay temas disponibles'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemas.map(tema => (
              <div
                key={tema.id}
                onClick={() => handleTemaClick(tema.id)}
                className={`rounded-lg shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer border overflow-hidden ${
                  darkMode 
                    ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' 
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <BookOpen size={20} className="text-purple-600 mr-2" />
                      <span className={`text-sm uppercase tracking-wider ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {tema.title.charAt(0)}
                      </span>
                    </div>
                  </div>
                  
                  <h3 className={`text-xl font-semibold mb-4 ${
                    darkMode ? 'text-white' : 'text-gray-800'
                  }`}>
                    {tema.title}
                  </h3>
                  
                  <div className="flex items-center text-purple-600 hover:text-purple-700 transition-colors">
                    <span className="text-sm font-medium">Explorar tema</span>
                    <ExternalLink size={16} className="ml-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Results count */}
        {filteredTemas.length > 0 && (
          <div className={`text-center mt-8 ${
            darkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            <p>
              Mostrando {filteredTemas.length} de {temas.length} temas
              {(searchTerm || selectedLetter !== 'Todos') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedLetter('Todos');
                  }}
                  className="ml-2 text-purple-600 hover:text-purple-700 underline transition-colors"
                >
                  Limpiar filtros
                </button>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemasPage;